<script setup lang="ts">
import {
  computed, onMounted, ref, watch, inject,
} from 'vue';
import { CdrContainer } from '@rei/cedar';

import QuickTools from '../../../components/quickTools/QuickTools.vue';
import CardLinkList from './CardLinkList.vue';
import OrderDetails from '../../../components/orders/OrderDetails.vue';
import HelpForYouSection from './HelpForYouSection.vue';

import { CardLinksKeys, type CardLinksProps } from '../models/interfaces/CardLinksComponent';
import { useUserAccountData } from '../../../common/composables/useUserAccountData';
import { PopularTopicsSection } from '../../../api/types/PopularTopicsSection';
import MembershipSummary from './MembershipSummary.vue';
import { apiCall } from '../../../api/services/apiCalls';
import useActiveReturns from '../composables/useActiveReturns';
import useReturnOrders from '../composables/useReturnOrders';
import useNewMember from '../../../common/composables/useNewMember';
import useRewardsBalance from '../../../common/composables/useRewardsBalance';

const store: any = inject('store');
const props: CardLinksProps = store.getValues(CardLinksKeys);

const popularTopicsSectionList = ref<PopularTopicsSection[] | null>(null);

const showQT = ref(true);
const showOD = ref(props.showOrderDetails);
const userInfo = ref({
  isLoggedIn: false,
  isMember: false,
  firstName: '',
  lastName: '',
  dividendBalance: undefined,
  memberJoinDate: undefined,
  memberNumber: undefined,
});

// The return cards fetch their own orders rather than reusing the list the
// orders widget reports back: that one is capped by maxNoOfOrders, and the cap
// gets spent on recent orders whether or not they have a return. The time
// window is also its own Conman property, separate from My Orders, since a
// return can stay active well past the window My Orders looks at.
const { orders: returnOrders } = useReturnOrders({
  showPreviousOrderDays: props.returnCardsPreviousOrderDays,
  statusForOrdersNotShown: props.statusForOrdersNotShown,
  userInfo,
});

const activeReturns = useActiveReturns(returnOrders, {
  narvarBaseUrl: props.narvarBaseUrl,
  activeReturnDays: props.returnCardsActiveReturnDays,
});

// Resolved here rather than inside the card so HelpForYouSection can count its
// cards before laying out, the same way it does with the returns list.
const { isNewMember } = useNewMember(
  () => userInfo.value,
  () => props.newMemberWelcomeDays,
);

const showNewMemberWelcome = computed(
  () => Boolean(props.showNewMemberWelcome) && isNewMember.value,
);

const { hasRewards, formattedBalance } = useRewardsBalance(() => userInfo.value);

const showRewardsCard = computed(
  () => Boolean(props.showRewardsCard) && hasRewards.value,
);

const checkQuickToolsState = () => {
  const { showOnArticlePage, showOnCategoryPage } = props.showQuickToolsOnPage;
  if (props.selectedCategory) {
    showQT.value = showOnArticlePage;
    return;
  }
  showQT.value = showOnCategoryPage;
};

onMounted(async () => {
  userInfo.value = await useUserAccountData();
  checkQuickToolsState();
  popularTopicsSectionList.value = await apiCall('default', 'popular-topics');
});

watch(() => userInfo.value, (newValue) => {
  const newState = { ...newValue };
  if (newState?.isLoggedIn) {
    showOD.value = props.showOrderDetails;
  } else {
    showQT.value = props.showQuickToolsOnPage.showOnCategoryPage;
  }
}, { immediate: true });

const checkOrderLength = (ordersList: string | any[]) => {
  showQT.value = ordersList && ordersList.length <= 0 as any;
  showOD.value = (ordersList && ordersList.length > 0) ? props.showOrderDetails : false;
};
</script>

<template>
  <div
    id="help-center-main-card-links"
    class="help-center-main-card-links"
  >
    <MembershipSummary :user-info="userInfo" />
    <OrderDetails
      v-if="showOD && userInfo?.isLoggedIn"
      :user-info="userInfo"
      :check-order-length="(orders: string | any[]) => checkOrderLength(orders)"
    />
    <!-- HelpForYouSection is the one place that knows about every card type
         shown here (returns, rewards and member welcome). It mounts
         a single HelpCardsCarousel so all cards scroll together rather than as
         separate rows — see ./HelpForYouSection.vue and ./HelpCardsCarousel.vue.
         Each card type carries its own Conman flag; the section hides itself
         when none of them has anything to show. -->
    <HelpForYouSection
      v-if="userInfo?.isLoggedIn"
      :returns="props.showReturnCards ? activeReturns : []"
      :show-new-member-welcome="showNewMemberWelcome"
      :show-rewards-card="showRewardsCard"
      :rewards-amount="formattedBalance"
      :card-order="props.helpForYouCarouselOrder"
    />
    <CdrContainer
      tag="section"
      class="help-center__card-section"
    >
      <div class="help-center__card-links">
        <QuickTools v-if="showQT" />
        <div
          class="help-center__card-links-sales"
          v-for="popularTopicsSection in popularTopicsSectionList"
          :key="popularTopicsSection.key"
        >
          <CardLinkList :popular-topics-section="popularTopicsSection" />
        </div>
      </div>
    </CdrContainer>
  </div>
</template>

<style scoped></style>
